import dayjs, { Dayjs } from "dayjs";

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { Box, Button, TextField, Typography } from "@mui/material";

import { PRO_ALERT_LIMIT } from "@/constants/membership";
import { createAlertAction, getUsageAction, modifyAlertAction } from "@/api/alert";
import ProChip from "@/components/ProChip";
import SearchRangeDate from "@/components/SearchBar/SearchRangeDate";
import { AlertTypeEnum } from "@/constants";
import { AREA_CODES } from "@/constants/areaCode";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";
import GradeRoundedIcon from '@mui/icons-material/GradeRounded';
import useSnackbar from "@/hooks/useSnackbar";
import useThemeData from "@/hooks/useThemeData";

import { useAlertContext } from "./AlertContext";
import FormItem from "./FormItem";
import FormPhoneItem, { validatePhone } from "./FormPhoneItem";
import SearchHotel from "./SearchHotel/index";

interface FromData {
  alert_type: string;
  alertId?: string;
  fromLocation: string[];
  toLocation: string[];
  departureDate: number;
  departureDateSec: number;
  maxPoints: string;
  valid: boolean;
  // areaCode: string;
  // phoneNumber: string;
  infiniteMode: boolean;
  hotelName: string[];
  areaCode: string;
  phoneNumber: string;
  agreeReceiveSMS: boolean;
}

export interface IRef {
  show: (data: FromData) => void;
  submit: () => void;
}

interface PanoramaFormProps {
  onSuccess?: () => void;
  onReset?: () => void;
}

const PanoramaForm = forwardRef<IRef, PanoramaFormProps>((props, ref) => {
  const theme = useThemeData();
  const { isMobile } = usePlatform();
  const { enqueueSnackbar } = useSnackbar();

  const {
    setLoading,
    open,
    setOpen,
    setOpenResult,
    setAlertModalInfo,
    infiniteMode,
    setInfiniteMode,
  } = useAlertContext();

  const initDataUsedAlerts = useRef(0);
  const [usageLoading, setUsageLoading] = useState(true);

  const [alertNeeded, setAlertNeeded] = useState(-1);
  const [alertUsageInfo, setAlertUsageInfo] = useState({
    alertLimit: 0,
    alertUsed: 0,
    alertLeft: 0,
  });

  const store = useGlobalStoreContext();
  const navigate = useNavigate();

  const externalsData = useRef<FromData>();

  const defaultFormData = useRef({
    alert_type: "hotel",
    alertId: "",
    fromLocation: [],
    toLocation: [],
    departureDate: 0,
    departureDateSec: 0,
    maxPoints: "",
    valid: true,
    // areaCode: AREA_CODES[0].dial_code,
    // phoneNumber: "",
    infiniteMode: false,
    hotelName: [],
    areaCode: AREA_CODES[0].dial_code,
    phoneNumber: "",
    agreeReceiveSMS: false,
  });

  const [formData, setFormData] = useState<FromData>(defaultFormData.current);

  const alertsInfo = useMemo(() => {
    let text = "";
    let color = theme.palette.success.main;
    let icon = <NotificationsActiveIcon sx={{ color }} fontSize="small" />;

    const { alertLeft } = alertUsageInfo;

    if (alertNeeded >= 0) {
      text += `${alertNeeded} active alerts needed, `;
    }

    if (alertLeft >= 0) {
      text += `${alertLeft} active alerts left`;
    }

    if (alertNeeded >= 0 && alertLeft >= 0) {
      if (alertNeeded > alertLeft) {
        color = theme.palette.error.main;
        icon = <ErrorOutlinedIcon sx={{ color }} fontSize="small" />;
      } else {
        icon = <CheckCircleOutlineIcon sx={{ color }} fontSize="small" />;
      }
    }

    return { text, color, icon };
  }, [alertUsageInfo, alertNeeded, theme.palette]);

  const handleCreate = useEvent(async () => {
    const maxPointsStr = (formData.maxPoints || "").trim();

    let message = "";

    if (!formData.fromLocation.length) {
      message = "Hotel cannot be empty.";
    } else if (!formData.departureDate || !formData.departureDateSec) {
      message = "Date cannot be empty.";
    } else if (!maxPointsStr) {
      message = "Max points per night on average cannot be empty.";
    } else if (isNaN(Number(maxPointsStr))) {
      message = "Max Points per night on average need to be a number.";
    }

    if (formData.phoneNumber && formData.areaCode) {
      const valid = validatePhone(formData);

      if (!valid) {
        message = "Invalid phone number.";
      }

      if (!formData.agreeReceiveSMS) {
        message = "Please agree to receive SMS based on your data";
      }
    }

    if (message) {
      enqueueSnackbar(message);
      return;
    }

    try {
      const params: any = {
        filter_criteria: {
          max_miles: Number(formData.maxPoints.trim()),
        },
        info: {
          departure: formData.fromLocation,
          arrival: formData.fromLocation,
          departureDate: dayjs(formData.departureDate).format("YYYY-MM-DD"),
          departureDateSec: dayjs(formData.departureDateSec).format("YYYY-MM-DD"),
          passengers: 1,
          hotelName: formData.hotelName,
        },
        alert_type: AlertTypeEnum.Hotel,
        infinite_mode: infiniteMode,
        phone_number: {
          number: formData.phoneNumber,
          area: formData.areaCode,
          agreeReceiveSMS: formData.agreeReceiveSMS,
        },
      };

      setLoading(true);

      if (formData.alertId) {
        await modifyAlertAction({ ...params, alert_id: formData.alertId });

        props.onSuccess && props.onSuccess();
        setOpen(false);
      } else {
        await createAlertAction(params);

        setAlertModalInfo({
          alertType: AlertTypeEnum.Hotel,
          isSuccess: true,
          fromLocation: formData.hotelName,
          toLocation: formData.hotelName,
          departureDate: formData.departureDate,
          departureDateSec: formData.departureDateSec,
        });

        props.onSuccess && props.onSuccess();

        setOpen(false);
        setOpenResult(true);
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.indexOf("Exceed alert limit") !== -1) {
          setAlertModalInfo({
            isSuccess: false,
            alertLimit: alertUsageInfo.alertLimit,
          });

          setOpen(false);
          setOpenResult(true);
        } else {
          enqueueSnackbar(error.message || "");
        }
      }
    } finally {
      setLoading(false);
    }
  });

  const handleDepartureDateChange = useEvent((date: Array<Dayjs | null>) => {
    setFormData({
      ...formData,
      departureDate: date[0] ? dayjs(date[0]).valueOf() : 0,
      departureDateSec: date[1] ? dayjs(date[1]).valueOf() : 0,
    });
  });

  const promptUpsellUI = () => {
    return store?.show_upsell ? (
      <Typography
        component={Button}
        variant="body2"
        color="primary"
        onClick={() => {
          window.open("/pro", "_blank", "noopener,noreferrer");
        }}
        sx={{marginTop: "-5px", paddingLeft: "10px", marginLeft: "10px", lineHeight: "16px" }}
      >
        <div className="mr-[6px]">
          <ProChip height="18px"/>
        </div>
        Get {PRO_ALERT_LIMIT} alerts now!
      </Typography>
    ) : null;
  };

  const getUsage = useEvent(() => {
    setUsageLoading(true);
    getUsageAction()
      .then((res) => {
        const alertLeft = res.max_alert - res.alert_set;

        setAlertUsageInfo({
          alertLimit: res.max_alert,
          alertUsed: res.alert_set,
          alertLeft: alertLeft + initDataUsedAlerts.current,
        });
        setUsageLoading(false);
      })
      .catch(() => {
        setUsageLoading(false);
      });
  });

  const handleShow = (data: FromData) => {
    if (data) {
      externalsData.current = data;

      const newData = {
        ...defaultFormData.current,
        ...(data || {}),
      };

      setFormData(newData);

      setInfiniteMode(data.infiniteMode || false);

      if (data.alertId && data.valid) {
        const usedAlerts =
          (dayjs(data.departureDateSec).startOf("day").diff(data.departureDate, "day") + 1) *
          data.fromLocation.length;

        initDataUsedAlerts.current = usedAlerts;
      }
      setOpen(true);
    }
  };

  const handleClose = useEvent(() => {
    props.onReset && props.onReset();
  });

  useEffect(() => {
    if (open) {
      getUsage();
    } else {
      setTimeout(() => {
        setFormData(defaultFormData.current);
        setAlertUsageInfo({ alertLimit: 0, alertUsed: 0, alertLeft: -1 });
        setAlertNeeded(-1);
        handleClose();
      }, 500);
    }
  }, [open, getUsage, handleClose]);

  useEffect(() => {
    if (formData.departureDate && formData.departureDateSec && formData.fromLocation.length) {
      const count =
        (dayjs(formData.departureDateSec).startOf("day").diff(formData.departureDate, "day") + 1) *
        formData.fromLocation.length;

      setAlertNeeded(count);
    } else {
      setAlertNeeded(-1);
    }
  }, [formData.departureDate, formData.departureDateSec, formData.fromLocation]);

  useImperativeHandle(ref, () => ({
    show: handleShow,
    submit: handleCreate,
  }));

  return (
    <div>
      <Box className="mt-[16px]">
        <SearchHotel
          selectData={formData.fromLocation.map((v, index) => ({
            label: formData.hotelName[index],
            value: v,
          }))}
          onChange={(data) => {
            setFormData({
              ...formData,
              fromLocation: data.map(({ value }) => value),
              hotelName: data.map(({ label }) => label),
            });
          }}
          placeholder="hotel"
        />
      </Box>

      <Box className="mt-[16px]">
        <SearchRangeDate
          label="First stay - Last stay"
          defaultValue={[
            formData.departureDate ? dayjs(formData.departureDate) : null,
            formData.departureDateSec ? dayjs(formData.departureDateSec) : null,
          ]}
          onChange={handleDepartureDateChange}
        />
      </Box>

      {!usageLoading && alertsInfo.text ? (
        <Box className="mb-[6px] mt-[16px] flex items-center">
          {alertsInfo.icon}
          <Typography
            variant="body2"
            sx={{ color: alertsInfo.color, paddingLeft: "5px", lineHeight: "16px" }}
          >
            {alertsInfo.text}
          </Typography>
          {!isMobile ? promptUpsellUI() : null}
        </Box>
      ) : (
        <div className="h-[16px]" />
      )}
      {isMobile ? promptUpsellUI() : null}

      {/* <FormPhoneItem
        formData={formData}
        show_upsell={store.show_upsell}
        setFormData={(value) => setFormData({ ...formData, ...value })}
      /> */}
      <FormItem label={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GradeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
          Max Average Points per Night *:
        </Box>
      }>
        <TextField
          placeholder="Required"
          variant="outlined"
          size="small"
          type="number"
          value={formData.maxPoints}
          onChange={(e) => {
            setFormData({ ...formData, maxPoints: e.target.value });
          }}
          inputProps={{ style: { fontSize: "14px", width: "80px" }, min: 0 }}
        />
      </FormItem>
    </div>
  );
});

PanoramaForm.displayName = "PanoramaForm";

export default PanoramaForm;
